/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectorRef, Component, computed, input, OnInit, Optional, output, Self, signal } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'eiq-rich-text-input',
  templateUrl: 'rich-text-input.component.html',
  styleUrls: ['rich-text-input.component.scss']
})
export class RichTextInputComponent implements OnInit, ControlValueAccessor {
  private static instanceCount = 0;

  placeholder = input<string>('');
  keyboardHint = input<string>('');
  showCharCount = input<boolean>(true);
  showKeyboardHint = input<boolean>(true);
  showSubmitButton = input<boolean>(true);
  submitLabel = input<string>('Post reply');
  testId = input<string>();

  submitted = output<void>();

  onChange: (value: string) => void;
  onTouched: () => void;

  control: FormControl;
  editor: any = DecoupledEditor;
  config: any;

  focused$ = signal<boolean>(false);
  charCount$ = signal<number>(0);
  isDisabled$ = signal<boolean>(false);
  isAdvancedMode$ = signal<boolean>(false);

  private readonly isMac = /Macintosh/.test(navigator.userAgent);
  displayHint$ = computed(() => this.keyboardHint() || (this.isMac ? '⌘↵ to send' : 'Ctrl↵ to send'));

  faPenToSquare = faPenToSquare;
  faPaperPlaneTop = faPaperPlaneTop;

  readonly uniqueId = `rti-${++RichTextInputComponent.instanceCount}`;

  private editorInstance: any;
  private pendingValue: string | null = null;

  constructor(
    @Optional() @Self() public ngControl: NgControl | null,
    private cdr: ChangeDetectorRef
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.control = (this.ngControl?.control as FormControl) ?? null;
    this.config = {
      placeholder: this.placeholder(),
      toolbar: {
        items: ['|', 'bold', 'italic', 'underline', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo']
      },
      image: { resizeUnit: 'px', toolbar: [] },
      table: { contentToolbar: [] },
      list: { properties: { styles: true, reversed: true, startIndex: true } },
      language: 'en'
    };
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    const safeValue = value ?? '';
    this.charCount$.set(this.getPlainText(safeValue).length);
    if (this.editorInstance) {
      this.editorInstance.setData(safeValue);
    } else {
      this.pendingValue = safeValue;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled$.set(isDisabled);
    if (this.editorInstance) {
      if (isDisabled) {
        this.editorInstance.enableReadOnlyMode('cva-disabled');
      } else {
        this.editorInstance.disableReadOnlyMode('cva-disabled');
      }
    }
  }

  toggleAdvancedMode(): void {
    this.isAdvancedMode$.set(!this.isAdvancedMode$());
  }

  onReady(editor: any): void {
    this.editorInstance = editor;

    const toolbarContainer = document.getElementById(`${this.uniqueId}-toolbar`);
    if (toolbarContainer) {
      toolbarContainer.appendChild(editor.ui.view.toolbar.element);
    }

    if (this.pendingValue !== null) {
      editor.setData(this.pendingValue);
      this.charCount$.set(this.getPlainText(this.pendingValue).length);
      this.pendingValue = null;
    }

    if (this.isDisabled$()) {
      editor.enableReadOnlyMode('cva-disabled');
    }

    editor.ui.focusTracker.on('change:isFocused', (_evt: any, _name: any, isFocused: boolean) => {
      this.focused$.set(isFocused);
      if (!isFocused && this.onTouched) {
        this.onTouched();
      }
      this.cdr.detectChanges();
    });

    editor.model.document.on('change:data', () => {
      const data = editor.getData();
      this.charCount$.set(this.getPlainText(data).length);
      if (this.onChange) {
        this.onChange(data);
      }
      this.cdr.detectChanges();
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  onSubmit(): void {
    this.submitted.emit();
  }

  private getPlainText(html: string): string {
    if (!html) {
      return '';
    }
    return html.replace(/<[^>]*>/g, '').trim();
  }
}
